/**
 * Inbox → venue Acts Google Sheet sync (Half-claw F2).
 * Sunset + Craic-Up; Edge routes each slug to its own Apps Script URL/secret.
 * @see prototype/sheet-lineup-sync/README.md
 */

/** Venues whose roster sheet supports write-back in F2. Keep in sync with Edge allowlist. */
export const SHEET_LINEUP_SYNC_VENUE_SLUGS = ['sunset', 'craicup'];

/** Offer writes use Sunset column headers (F/G = 10s). Craic layout differs — hold until that profile exists. */
export const SHEET_LINEUP_OFFER_VENUE_SLUGS = ['sunset'];

export const INBOX_UPDATE_SPREADSHEET_STORAGE_KEY = 'inbox_update_spreadsheet';

export function isSheetLineupSyncVenue(venueSlug) {
  const slug = String(venueSlug || '')
    .toLowerCase()
    .trim();
  return SHEET_LINEUP_SYNC_VENUE_SLUGS.includes(slug);
}

export function isSheetLineupOfferVenue(venueSlug) {
  const slug = String(venueSlug || '')
    .toLowerCase()
    .trim();
  return SHEET_LINEUP_OFFER_VENUE_SLUGS.includes(slug);
}

/** Edge secret names for this venue. Keep in sync with sheet-lineup-sync-forward.ts */
export function sheetLineupSyncEnvKeys(venueSlug) {
  const slug = String(venueSlug || '')
    .toLowerCase()
    .trim();
  if (slug === 'craicup') {
    return {
      url: 'SHEET_LINEUP_WEB_APP_URL_CRAICUP',
      secret: 'SHEET_SYNC_SECRET_CRAICUP',
    };
  }
  return {
    url: 'SHEET_LINEUP_WEB_APP_URL',
    secret: 'SHEET_SYNC_SECRET',
  };
}

export function readInboxUpdateSpreadsheetPref() {
  try {
    const raw = localStorage.getItem(INBOX_UPDATE_SPREADSHEET_STORAGE_KEY);
    if (raw === '0' || raw === 'false') return false;
    if (raw === '1' || raw === 'true') return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function saveInboxUpdateSpreadsheetPref(enabled) {
  try {
    localStorage.setItem(INBOX_UPDATE_SPREADSHEET_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Build the candidate shape expected by {@link syncLineupSheetFromInbox} from Events page data.
 *
 * @param {object} application
 * @param {object} [event] - row from Admin Dates `events` (includes `venues`)
 */
export function buildSheetSyncCandidate(application, event) {
  const venues = event?.venues || {};
  return {
    comedian_name: application.comedian_name,
    event: {
      event_date: event?.event_date,
      venue_slug: venues.venue_slug,
      venue_name: venues.venue_name,
    },
  };
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ cand: object, action: 'confirm'|'reject'|'offer'|'read', spot?: { spotCode?: string, isBringer?: boolean } }} params
 * @returns {Promise<{ ok: boolean, skipped?: boolean, reason?: string, cell_not_found?: boolean, no_empty_slot?: boolean, sheet?: object, cellText?: string, hasProvisional?: boolean }>}
 */
export async function syncLineupSheetFromInbox(supabase, { cand, action, spot }) {
  const venueSlug = cand?.event?.venue_slug;
  if (!isSheetLineupSyncVenue(venueSlug)) {
    return { ok: false, skipped: true, reason: 'venue_not_in_scope' };
  }
  if (action === 'offer' && !isSheetLineupOfferVenue(venueSlug)) {
    return { ok: false, skipped: true, reason: 'venue_not_in_scope' };
  }

  const eventDate = cand?.event?.event_date;
  const comedianName = cand?.comedian_name;
  if (!eventDate || !comedianName) {
    return { ok: false, skipped: true, reason: 'missing_event_or_name' };
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { ok: false, skipped: true, reason: 'no_session' };
  }

  const body = {
    event_date: eventDate,
    comedian_name: comedianName,
    action,
    venue_slug: venueSlug,
  };
  if (action === 'offer' && spot) {
    if (spot.spotCode) body.spot_code = spot.spotCode;
    body.is_bringer = Boolean(spot.isBringer);
  }

  const { data, error } = await supabase.functions.invoke('sheet-lineup-sync', {
    body,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    console.warn('syncLineupSheetFromInbox: invoke failed', error);
    const msg = error.message || 'invoke_failed';
    if (msg.includes('non-2xx')) {
      return {
        ok: false,
        skipped: false,
        reason:
          'edge_non_2xx — redeploy sheet-lineup-sync or check Supabase Edge logs for sheet_lineup_sync',
      };
    }
    return { ok: false, skipped: false, reason: msg };
  }

  if (data?.skipped) {
    return { ok: false, skipped: true, reason: data.reason || 'not_configured' };
  }

  return {
    ok: Boolean(data?.ok),
    skipped: false,
    cell_not_found: Boolean(data?.cell_not_found),
    no_empty_slot: Boolean(data?.no_empty_slot) || data?.reason === 'no_empty_slot',
    unchanged: Boolean(data?.sheet?.unchanged),
    cellText: data?.sheet?.cellText,
    hasProvisional: Boolean(data?.sheet?.hasProvisional),
    sheet: data?.sheet,
    reason: data?.ok
      ? undefined
      : data?.reason || data?.sheet?.error || data?.error || 'sheet_sync_failed',
  };
}

/**
 * @param {{ ok: boolean, skipped?: boolean, cell_not_found?: boolean, reason?: string }} result
 * @param {{ enabled?: boolean }} [opts] - when enabled, surface skipped/failed sync (not silent)
 * @returns {string|null} User-facing note to append after DB success, or null if nothing to say.
 */
export function formatSheetSyncResultNote(result, opts = {}) {
  const enabled = opts.enabled !== false;
  if (!result || !enabled) return null;
  if (result.skipped) {
    if (result.reason === 'venue_not_in_scope') return null;
    if (result.reason === 'not_configured' || result.reason === 'sheet_sync_not_configured') {
      return 'Spreadsheet sync not configured on server (DB updated).';
    }
    if (result.reason === 'missing_event_or_name') {
      return 'Spreadsheet sync skipped — missing event date or name (DB updated).';
    }
    return null;
  }
  const sheetErr = result.sheet?.error || result.reason;
  if (sheetErr === 'unauthorized') {
    return 'Spreadsheet sync unauthorized — SHEET_SYNC_SECRET mismatch on Supabase vs Apps Script (DB updated).';
  }
  if (result.reason === 'bad_apps_script_response (HTTP 200)' || String(result.reason || '').includes('bad_apps_script')) {
    return 'Spreadsheet sync got HTML not JSON from Apps Script — check Web app URL (/exec), access "Anyone", and redeploy (DB updated).';
  }
  if (String(result.reason || '').startsWith('http_')) {
    return `Spreadsheet sync HTTP error (${result.reason}) — check SHEET_LINEUP_WEB_APP_URL (DB updated).`;
  }
  if (result.ok && result.unchanged) {
    return 'Spreadsheet: cell matched but text unchanged (DB updated). Redeploy Apps Script if you expected ? to be removed.';
  }
  if (result.ok) return 'Spreadsheet updated.';
  if (result.no_empty_slot || result.reason === 'no_empty_slot' || result.sheet?.error === 'no_empty_slot') {
    const spot = result.sheet?.spotCode || result.spot_code;
    const spotBit = spot ? ` for a ${spot} spot` : '';
    return `Spreadsheet: no empty slot${spotBit} for that date (DB updated).`;
  }
  if (result.cell_not_found) {
    return 'Spreadsheet: no matching cell (DB updated).';
  }
  return `Spreadsheet not updated (${result.reason || 'error'}). DB updated.`;
}
